// =============================================================================
// app/src/lib/comprovante.ts — o comprovante da conta: preparar e abrir.
//
// Imagem passa pelo MESMO compressor da foto de peça (comprimirParaJpeg):
// vira JPEG ~250 KB e perde o EXIF/GPS no aparelho. PDF vai como está, com o
// teto conferido aqui ANTES do upload — recusa legível na hora, não depois de
// meio minuto de 4G. O servidor confere de novo (magic bytes na RPC, 044).
// =============================================================================
import { comprimirParaJpeg } from './imagem';

/** Espelho do teto da RPC (044; ampliado para 2,5 MB na 057). Quem NEGA é o
 *  banco — isto aqui só evita subir 4G à toa para levar um erro no fim. */
export const TETO_COMPROVANTE = 2560 * 1024;

export type TipoComprovante = 'imagem' | 'pdf';

/** Decide o caminho pelo MIME do arquivo escolhido. Nulo = não aceitamos. */
export function tipoDoComprovante(mime: string): TipoComprovante | null {
  if (mime === 'application/pdf') return 'pdf';
  if (mime.startsWith('image/')) return 'imagem';
  return null;
}

export interface ComprovantePronto {
  base64: string;
  contentType: 'image/jpeg' | 'application/pdf';
  nome: string;
}

export async function prepararComprovante(arquivo: File): Promise<ComprovantePronto> {
  const tipo = tipoDoComprovante(arquivo.type);
  if (!tipo) {
    throw new Error('Comprovante é PDF ou imagem (foto do recibo, print do pix).');
  }
  if (tipo === 'imagem') {
    const img = await comprimirParaJpeg(arquivo);
    return { base64: img.base64, contentType: 'image/jpeg', nome: arquivo.name };
  }
  if (arquivo.size > TETO_COMPROVANTE) {
    throw new Error('O PDF passou de 2,5 MB — comprovante de banco é bem menor que isso. Confira se é o arquivo certo.');
  }
  const base64 = await new Promise<string>((resolve, reject) => {
    const leitor = new FileReader();
    leitor.onload = () => resolve(String(leitor.result).split(',')[1] ?? '');
    leitor.onerror = () => reject(new Error('Não consegui ler o arquivo — tente de novo.'));
    leitor.readAsDataURL(arquivo);
  });
  return { base64, contentType: 'application/pdf', nome: arquivo.name };
}

/** Base64 → blob → nova aba. O revoke fica para depois: o navegador precisa
 *  da URL viva enquanto a aba carrega. */
export function abrirComprovante(base64: string, contentType: string): void {
  const binario = atob(base64.replace(/\s/g, ''));
  const bytes = new Uint8Array(binario.length);
  for (let i = 0; i < binario.length; i += 1) bytes[i] = binario.charCodeAt(i);
  const url = URL.createObjectURL(new Blob([bytes], { type: contentType }));
  window.open(url, '_blank', 'noopener');
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
